import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { normalizeValue } from '../../../../../utils/Texts';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';
import AsignarCursosEstudiantesModal from './AsignarCursosEstudiantesModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Asignación de Cursos a Estudiantes";
const endpoint = ENDPOINTS.estudiante;

export default function AsignarCursosEstudiantesCatalog() {
    const { puedeEditar } = useResourcePermissions('LMS_ESTUDIANTES_CURSOS');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]); // Estudiantes
    const [empleados, setEmpleados] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [studentName, setStudentName] = useState('');

    // Cargar estudiantes desde BD
    const loadData = async () => {
        setLoading(true);
        try {
            const [resEstudiantes, resCounts] = await Promise.all([
                axios.get(endpoint),
                axios.get(ENDPOINTS.estudianteCursoConteos).catch(() => ({ data: [] }))
            ]);
            
            const conteos = resCounts.data || [];
            const estudiantes = (resEstudiantes.data || []).map((x) => {
                const mapped = Models.estudiante(x);
                const conteo = conteos.find(c => Number(c.idEstudiante || c.IdEstudiante) === Number(mapped.idEstudiante));
                mapped.cantidadCursos = conteo ? (conteo.cantidadCursos || conteo.CantidadCursos || 0) : 0;
                return mapped;
            });
            setRows(estudiantes);
        } catch (error) {
            console.error('Error al cargar estudiantes:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    // Cargar empleados y personas para el modal (copiados de módulo General)
    const loadEmpleadosPersonas = async () => {
        try {
            const [resEmp, resPer] = await Promise.all([
                axios.get(ENDPOINTS.empleados),
                axios.get(ENDPOINTS.personas),
            ]);
            setEmpleados((resEmp.data || []).map((x) => Models.empleado(x)));
            setPersonas((resPer.data || []).map((x) => Models.personas(x)));
        } catch (error) {
            console.error('Error al cargar empleados/personas:', error);
        }
    };

    useEffect(() => {
        loadData();
        loadEmpleadosPersonas();
    }, []);

    // Helper: nombre completo de empleado
    const getEmpleadoNombre = useCallback((idEmpleado) => {
        const emp = empleados.find((e) => e.idEmpleado === idEmpleado);
        if (!emp) return `Empleado ${idEmpleado}`;
        const per = personas.find((p) => p.idPersona === emp.idPersona);
        return per
            ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim()
            : `Empleado ${idEmpleado}`;
    }, [empleados, personas]);

    // Filtrado
    const filtered = useMemo(() => {
        const isSuperAdmin = isSessionSuperAdmin();
        const sessionEmpresaId = getSessionEmpresaId();

        // 1. Aislamiento Multi-Tenant
        let data = rows.filter(r => {
            if (isSuperAdmin) return true;
            // Solo ver lo de mi empresa o lo que sea Global (idEmpresa null/0)
            return r.idEmpresa === sessionEmpresaId || !r.idEmpresa || r.idEmpresa === 0;
        });

        // 2. Filtro de búsqueda
        if (query.trim()) {
            const q = query.toLowerCase().trim();
            data = data.filter((e) => {
                const empNombre = getEmpleadoNombre(e.idEmpleado).toLowerCase();
                const nick = normalizeValue(e.nickname).toLowerCase();
                const empresa = (e.nombreEmpresa || '').toLowerCase();
                return (
                    empNombre.includes(q) ||
                    nick.includes(q) ||
                    empresa.includes(q)
                );
            });
        }

        return data;
    }, [rows, query, getEmpleadoNombre]);

    // Paginación
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    // Abrir modal de asignación
    const handleAssign = (id, name) => {
        setSelectedId(id);
        setStudentName(name);
        setModalOpen(true);
    };

    return (
        <>
            <div className="panel-sigma-main">
                <div className="roles-shell">
                    <div className="roles-topbar">
                        <h2 className="roles-title">{nombreTabla}</h2>

                        <input
                            className="roles-search"
                            placeholder="Buscar estudiante por nombre o nickname..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    {loading ? (
                        <div className="catalog-loading">Cargando estudiantes...</div>
                    ) : (
                        <div className="roles-table-wrap">
                            <div className="roles-table-scroller">
                                <table className="roles-table">
                                    <thead>
                                        <tr>
                                            <th>Empleado</th>
                                            <th>Nickname</th>
                                            <th>Empresa</th>
                                            <th style={{ textAlign: 'center' }}>Cursos Configurados</th>
                                            {puedeEditar && <th style={{ textAlign: 'center' }}>Asignar Cursos</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan={puedeEditar ? 5 : 4} style={{ textAlign: 'center' }}>
                                                    No se encontraron estudiantes.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((e) => {
                                                const name = getEmpleadoNombre(e.idEmpleado);
                                                return (
                                                    <tr key={e.idEstudiante}>
                                                        <td>{name}</td>
                                                        <td>{normalizeValue(e.nickname)}</td>
                                                        <td>
                                                            <span style={{ fontSize: '0.85rem', color: e.idEmpresa ? '#334155' : '#94a3b8' }}>
                                                                {e.nombreEmpresa || 'Global'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{
                                                                background: e.cantidadCursos > 0 ? '#eff6ff' : '#f1f5f9',
                                                                color: e.cantidadCursos > 0 ? '#1d4ed8' : '#64748b',
                                                                padding: '4px 10px',
                                                                borderRadius: '999px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '600'
                                                            }}>
                                                                {e.cantidadCursos || 0}
                                                            </span>
                                                        </td>
                                                        {puedeEditar && (
                                                            <td style={{ textAlign: 'center' }}>
                                                                <button
                                                                    type="button"
                                                                    className="rk-iconbtn rk-iconbtn--edit"
                                                                    onClick={() => handleAssign(e.idEstudiante, name)}
                                                                    aria-label="Asignar Cursos"
                                                                    title="Asignar Cursos"
                                                                >
                                                                    <img src={sigmaEdit} alt="" className="rk-ico" />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="roles-pagination">
                        <div className="roles-pagination-left">
                            {filtered.length > 0
                                ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                                : <span>0 resultados</span>}
                        </div>
                        <div className="roles-pagination-right">
                            <PageBtn
                                label="‹"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                            />
                            {pageNumbers.map((n) => (
                                <PageBtn
                                    key={n}
                                    label={n}
                                    onClick={() => setPage(n)}
                                    active={n === page}
                                />
                            ))}
                            <PageBtn
                                label="›"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <AsignarCursosEstudiantesModal
                    open={modalOpen}
                    idEstudiante={selectedId}
                    studentName={studentName}
                    onClose={() => {
                        setModalOpen(false);
                        loadData();
                    }}
                />
            )}
        </>
    );
}
